<template>
  <div class="min-h-screen">
    <!-- SEO Meta Tags -->
    <PageSEO
      title="Web Development Services - Full-Stack Solutions"
      description="Complete web application development from concept to deployment. We build scalable, maintainable, and high-performance web solutions using modern technologies."
      :keywords="['web development', 'full-stack development', 'web applications', 'responsive design', 'scalable architecture', 'modern web technologies']"
      url="https://vedware.com/services/web-development"
    />

    <!-- Hero Section -->
    <HeroSection
      :icon="PhGlobe"
      title="Web"
      highlighted-title="Development"
      description="Complete web application development from concept to deployment. We build scalable, maintainable, and high-performance web solutions tailored to your business needs."
    />

    <!-- Service Overview -->
    <ServiceOverview
      :description="[
        'Our web development service covers the entire application lifecycle, from initial planning and design to deployment and ongoing maintenance. We specialize in creating modern, responsive web applications that provide exceptional user experiences.',
        'Whether you need a simple business website, a complex e-commerce platform, or a custom web application, we have the expertise to bring your vision to life using the latest technologies and best practices.',
      ]"
      :features="[
        'Full-stack development from frontend to backend',
        'Responsive design for all devices',
        'Scalable architecture and performance optimization',
        'Security best practices and data protection',
        'SEO optimization and accessibility compliance',
        'Ongoing support and maintenance',
      ]"
    />

    <!-- Technologies Section -->
    <ServiceTechnologies
      title="Technologies We Use"
      description="We leverage a comprehensive stack of modern technologies to build robust, scalable web applications."
      :categories="technologyCategories"
    />

    <!-- Process Section -->
    <div class="py-20">
      <div class="container mx-auto px-6">
        <div class="mx-auto max-w-7xl">
          <div class="mb-16 text-center">
            <h2 class="mb-6 text-3xl font-bold text-ternary-dark dark:text-primary-light sm:text-4xl">
              Our Development Process
            </h2>
            <p class="mx-auto max-w-3xl text-lg text-gray-600 dark:text-gray-400">
              We follow a structured approach to ensure your project is delivered on time, within budget, and exceeds expectations.
            </p>
          </div>

          <div class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            <div
              v-for="(step, index) in processSteps"
              :key="index"
              class="relative text-center"
            >
              <div class="mb-6 flex justify-center">
                <div class="flex size-16 items-center justify-center rounded-2xl bg-gradient-to-r from-accent to-gradient-end shadow-lg">
                  <component :is="step.icon" class="size-8 text-white" />
                </div>
              </div>
              <h3 class="mb-3 text-xl font-bold text-ternary-dark dark:text-ternary-light">
                {{ step.title }}
              </h3>
              <p class="text-gray-600 dark:text-gray-400">
                {{ step.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ServiceProjects slug="web-development" />

    <!-- CTA Section -->
    <ServiceCTA
      title="Ready to Build Your"
      highlighted-title="Web Application?"
      description="Let's discuss your project requirements and create a custom solution that drives your business forward."
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from '#imports'
import { PhCloud, PhCode, PhDatabase, PhGlobe, PhLayout, PhMagnifyingGlass, PhMonitor, PhRocket } from '@phosphor-icons/vue'
import ServiceCTA from '~/components/Services/ServiceCTA.vue'
import ServiceOverview from '~/components/Services/ServiceOverview.vue'
import ServiceProjects from '~/components/Services/ServiceProjects.vue'
import ServiceTechnologies from '~/components/Services/ServiceTechnologies.vue'
import HeroSection from '~/components/Shared/HeroSection.vue'
import PageSEO from '~/components/Shared/PageSEO.vue'

const technologyCategories = ref([
  {
    title: 'Frontend',
    icon: PhMonitor,
    technologies: [
      'HTML5',
      'CSS3',
      'JavaScript',
      'TypeScript',
      'Vue',
      'Nuxt.js',
      'React',
      'Svelte',
      'Tailwind CSS',
      'Bootstrap',
      'Sass',
      'jQuery',
      'Vuex',
      'Pinia',
      'Webpack',
      'Vite',
    ],
  },
  {
    title: 'Backend',
    icon: PhDatabase,
    technologies: [
      'PHP',
      'Laravel',
      'Node.js',
      'Express.js',
      'Python',
      'MySQL',
      'PostgreSQL',
      'MongoDB',
      'Redis',
      'REST APIs',
      'OAuth 2',
      'JWT',
      'Composer',
      'NPM',
      'Git',
      'Linux',
    ],
  },
  {
    title: 'Infrastructure',
    icon: PhCloud,
    technologies: [
      'Docker',
      'AWS',
      'Amazon EC2',
      'Amazon S3',
      'Apache',
      'NGINX',
      'Ubuntu Linux',
      'GitHub',
      'Bitbucket',
      'CI/CD Pipelines',
      'SSH',
      'Load Balancers',
    ],
  },
])

const processSteps = ref([
  {
    icon: PhMagnifyingGlass,
    title: 'Discovery',
    description: 'We analyze your requirements and define project scope, goals, and technical specifications.',
  },
  {
    icon: PhLayout,
    title: 'Design',
    description: 'Our team creates wireframes, mockups, and user experience designs for your application.',
  },
  {
    icon: PhCode,
    title: 'Development',
    description: 'We build your application using modern technologies and follow best coding practices.',
  },
  {
    icon: PhRocket,
    title: 'Deployment',
    description: 'We deploy your application to production and provide ongoing support and maintenance.',
  },
])
</script>
